import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import './AdminSmtpSettings.css';

function AdminSmtpSettings() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [testEmail, setTestEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    host: 'smtp.gmail.com',
    port: 587,
    secure: false,
    user: '',
    password: '',
    from_email: '',
    from_name: 'Magtic Parduotuvė',
    is_active: false,
  });

  const { data: settings, isLoading } = useQuery({
    queryKey: ['admin-smtp-settings'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/smtp-settings');
      return res.data;
    },
  });

  React.useEffect(() => {
    if (settings) {
      setFormData({
        host: settings.host || 'smtp.gmail.com',
        port: settings.port || 587,
        secure: settings.secure === 1,
        user: settings.user || '',
        password: '', // Niekada neįkelti – įvedamas tik keičiant
        from_email: settings.from_email || '',
        from_name: settings.from_name || 'Magtic Parduotuvė',
        is_active: settings.is_active === 1,
      });
    }
  }, [settings]);

  const updateMutation = useMutation({
    mutationFn: async (data) => {
      const res = await apiClient.put('/admin/smtp-settings', data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-smtp-settings']);
      notification.success('SMTP nustatymai sėkmingai išsaugoti');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida išsaugant SMTP nustatymus');
    },
  });

  const testMutation = useMutation({
    mutationFn: async (email) => {
      const res = await apiClient.post('/admin/smtp-settings/test', { test_email: email });
      return res.data;
    },
    onSuccess: () => {
      notification.success('Testinis el. laiškas sėkmingai išsiųstas! Patikrinkite savo el. pašto dėžutę.');
      setTestEmail('');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || error.response?.data?.message || 'Nepavyko išsiųsti testinio el. laiško');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Siųsti slaptažodį tik jei jis įvestas (keičiami autentifikacijos duomenys)
    const payload = { ...formData };
    if (!payload.password || payload.password.trim() === '') {
      delete payload.password;
    }
    updateMutation.mutate(payload);
  };

  const handleTest = () => {
    if (!testEmail) {
      notification.warning('Įveskite el. pašto adresą');
      return;
    }
    if (!formData.is_active) {
      notification.info('Pirmiausia įjunkite SMTP nustatymus');
      return;
    }
    testMutation.mutate(testEmail);
  };

  if (isLoading) return <div className="admin-smtp-settings-page container">Kraunama...</div>;

  return (
    <div className="admin-smtp-settings-page container">
      <div className="smtp-header">
        <h1>SMTP El. Pašto Nustatymai</h1>
        <p className="smtp-subtitle">
          Konfigūruokite SMTP serverį el. laiškų siuntimui. Sistema naudoja šiuos nustatymus vietoj .env failo.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="smtp-settings-form">
        <div className="form-section">
          <h2>SMTP Serverio Nustatymai</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>SMTP Serveris (Host) *</label>
              <input
                type="text"
                value={formData.host}
                onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                placeholder="smtp.gmail.com"
                required
              />
              <small>Pvz.: smtp.gmail.com, smtp.mailtrap.io, smtp.sendgrid.net</small>
            </div>
            <div className="form-group">
              <label>Portas *</label>
              <input
                type="number"
                value={formData.port}
                onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) || 587 })}
                placeholder="587"
                required
              />
              <small>Dažniausiai: 587 (TLS) arba 465 (SSL)</small>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.secure}
                  onChange={(e) => setFormData({ ...formData, secure: e.target.checked })}
                />
                <span>Naudoti SSL (portas 465)</span>
              </label>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Autentifikacijos Duomenys</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Vartotojo vardas (User) *</label>
              <input
                type="text"
                value={formData.user}
                onChange={(e) => setFormData({ ...formData, user: e.target.value })}
                placeholder="your-email@gmail.com"
                required
              />
            </div>
            <div className="form-group">
              <label>Slaptažodis (Password)</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={settings?.user ? 'Palikite tuščią, jei nekeičiate' : 'Įveskite slaptažodį'}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="btn-toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? 'Slėpti' : 'Rodyti'}
                </button>
              </div>
              <small>Įveskite tik jei keičiate slaptažodį. Gmail: naudokite "App Password".</small>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Siuntėjo Informacija</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>El. Pašto Adresas (From) *</label>
              <input
                type="email"
                value={formData.from_email}
                onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
                placeholder="noreply@magtic.lt"
                required
              />
            </div>
            <div className="form-group">
              <label>Siuntėjo Vardas (From Name) *</label>
              <input
                type="text"
                value={formData.from_name}
                onChange={(e) => setFormData({ ...formData, from_name: e.target.value })}
                placeholder="Magtic Parduotuvė"
                required
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              <span>Aktyvuoti SMTP nustatymus</span>
            </label>
            <small>Jei neaktyvuota, sistema naudos .env failo nustatymus (jei jie egzistuoja)</small>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="btn btn-primary"
          >
            {updateMutation.isPending ? 'Išsaugoma...' : 'Išsaugoti Nustatymus'}
          </button>
        </div>
      </form>

      <div className="test-section">
        <h2>Testuoti SMTP Nustatymus</h2>
        <p>Išsiųskite testinį el. laišką, kad patikrintumėte ar nustatymai veikia:</p>
        <div className="test-form">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="test@example.com"
            className="test-email-input"
          />
          <button
            type="button"
            onClick={handleTest}
            disabled={testMutation.isPending || !formData.is_active}
            className="btn btn-secondary"
          >
            {testMutation.isPending ? 'Siunčiama...' : 'Siųsti Testinį El. Laišką'}
          </button>
        </div>
        {!formData.is_active && (
          <p className="test-warning">⚠️ Pirmiausia įjunkite SMTP nustatymus</p>
        )}
      </div>
    </div>
  );
}

export default AdminSmtpSettings;
